import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Card, Row, Col, Upload, Space, Tag, message } from 'antd';
import { PlusOutlined, UploadOutlined, MinusCircleOutlined, EyeOutlined, FileOutlined } from '@ant-design/icons';
import { getSkills, imagepath } from '../api';

const { Option } = Select;
const documentTypes = [
  { value: 'passport', label: 'Passport' },
  { value: 'visa', label: 'Visa' },
  { value: 'emirates_id', label: 'Emirates ID (Front)' },
  { value: 'emirates_id_back', label: 'Emirates ID (Back)' },
  { value: 'labour_card', label: 'Labour Card' },
  { value: 'driving_licence', label: 'Driving Licence' },
  { value: 'health_insurance', label: 'Health Insurance' },
  { value: 'workmen_insurance', label: 'Workmen Insurance' },
  { value: 'other', label: 'Other' },
];

const SubcontractorEmployeeForm = ({ initialValues, onSubmit, loading, mode = 'add', onCancel }) => {
  const [form] = Form.useForm();
  const [imagePreview, setImagePreview] = useState(null);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    getSkills().then(res => {
      if (res.success) setSkills(res.data);
    });

    if (initialValues) {
      const formattedValues = { ...initialValues };
      if (formattedValues.documents && formattedValues.documents.length > 0) {
        formattedValues.documents = formattedValues.documents.map(doc => {
          if (!doc.document_file) return { ...doc, document_file: [] };
          if (Array.isArray(doc.document_file)) return doc;

          const rawPath = typeof doc.document_file === 'string' ? doc.document_file : '';
          const fileUrl = rawPath.startsWith('http') ? rawPath : `${imagepath}${rawPath}`;
          const fileName = doc.original_filename || (rawPath ? rawPath.split('/').pop() : 'document');

          return {
            ...doc,
            document_file: [{
              uid: String(doc.id || Math.random()),
              name: fileName,
              status: 'done',
              url: fileUrl,
              thumbUrl: fileUrl,
            }]
          };
        });
      }

      if (formattedValues.profile_image && typeof formattedValues.profile_image === 'string') {
        formattedValues.profile_image = [{
          uid: '-1',
          name: formattedValues.profile_image.split('/').pop(),
          status: 'done',
          url: `${imagepath}${formattedValues.profile_image}`,
        }];
      } else if (!Array.isArray(formattedValues.profile_image)) {
        formattedValues.profile_image = [];
      }

      form.setFieldsValue(formattedValues);

      if (formattedValues.profile_image && formattedValues.profile_image.length > 0) {
        setImagePreview(formattedValues.profile_image[0].url);
      }
    }
  }, [initialValues, form]);

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: '#fff', padding: '40px 5vw' }}>
      <h2>{mode === 'edit' ? 'Edit Subcontractor Employee' : 'Add Subcontractor Employee'}</h2>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Row gutter={24}>
          <Col span={12}>
            <Card title="Personal Information" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="emp_code" label="Employee Code"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="email" label="Email" rules={[{ type: 'email' }]}><Input /></Form.Item></Col>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="Phone (UAE)"
                    getValueFromEvent={(e) => e.target.value.replace(/\D/g, '')}
                    rules={[
                      { required: true, message: 'Please enter Phone (UAE)' },
                      { pattern: /^[0-9]+$/, message: 'Phone number must contain numbers only' }
                    ]}
                  >
                    <Input inputMode="numeric" placeholder="e.g. 0501234567" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="native_phone"
                    label="Native Phone (Home Country)"
                    getValueFromEvent={(e) => e.target.value.replace(/\D/g, '')}
                    rules={[{ pattern: /^[0-9]+$/, message: 'Phone number must contain numbers only' }]}
                  >
                    <Input inputMode="numeric" placeholder="e.g. 919876543210" />
                  </Form.Item>
                </Col>
                <Col span={12}><Form.Item name="dob" label="Date of Birth"><Input type="date" /></Form.Item></Col>
                <Col span={12}><Form.Item name="address" label="Address"><Input /></Form.Item></Col>
                <Col span={12}>
                  <Form.Item
                    name="profile_image"
                    label="Profile Image"
                    valuePropName="fileList"
                    initialValue={[]}
                    getValueFromEvent={e => {
                      if (Array.isArray(e)) return e;
                      return e && e.fileList;
                    }}
                  >
                    <Upload listType="picture" maxCount={1} beforeUpload={() => false}>
                      <Button icon={<UploadOutlined />}>Select Image</Button>
                    </Upload>
                  </Form.Item>
                  {imagePreview && <img src={imagePreview} alt="Preview" style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }} />}
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Job Information" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="position" label="Position"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="skill_id" label="Skill"><Select allowClear>{skills.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}</Select></Form.Item></Col>
                <Col span={12}><Form.Item name="joining_date" label="Joining Date"><Input type="date" /></Form.Item></Col>
                <Col span={12}><Form.Item name="status" label="Status" rules={[{ required: true }]}><Select><Option value="active">Active</Option><Option value="inactive">Inactive</Option></Select></Form.Item></Col>
                <Col span={12}><Form.Item name="salary_type" label="Salary Type"><Select><Option value="monthly">Monthly</Option><Option value="hourly">Hourly</Option></Select></Form.Item></Col>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.salary_type !== curr.salary_type}>
                  {({ getFieldValue }) => getFieldValue('salary_type') === 'monthly' ? (
                    <Col span={12}><Form.Item name="base_salary" label="Base Salary"><Input type="number" /></Form.Item></Col>
                  ) : getFieldValue('salary_type') === 'hourly' ? (
                    <Col span={12}><Form.Item name="hourly_rate" label="Hourly Rate"><Input type="number" /></Form.Item></Col>
                  ) : null}
                </Form.Item>
                <Col span={12}><Form.Item name="over_time_rate" label="Overtime Rate"><Input type="number" /></Form.Item></Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Card title="Employee Documents" style={{ marginBottom: 24 }}>
          <Form.List name="documents">
            {(fields, { add, remove }) => (
              <div key="documents-container">
                {fields.map(({ key, name, ...restField }) => {
                  const docVal = form.getFieldValue(['documents', name, 'document_file']);
                  const existingFile = Array.isArray(docVal) && docVal.length > 0 ? docVal[0] : (typeof docVal === 'string' ? docVal : null);
                  const existingUrl = existingFile ? (existingFile.url || (typeof existingFile === 'string' ? (existingFile.startsWith('http') ? existingFile : `${imagepath}${existingFile}`) : null)) : null;
                  const existingName = existingFile ? (existingFile.name || (typeof existingFile === 'string' ? existingFile.split('/').pop() : 'Document')) : null;

                  return (
                    <Row key={key} gutter={12} align="top" style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px dashed #f0f0f0' }}>
                      <Col span={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'document_type']}
                          label="Document Type"
                          rules={[{ required: true, message: 'Document type is required' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select placeholder="Select type" style={{ width: '100%' }}>
                            {documentTypes.map(dt => <Option key={dt.value} value={dt.value}>{dt.label}</Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'issue_date']}
                          label="Issue Date"
                          getValueProps={(value) => ({
                            value: value ? new Date(value).toISOString().split('T')[0] : ''
                          })}
                          style={{ marginBottom: 0 }}
                        >
                          <Input type="date" placeholder="Issue Date" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'expiry_date']}
                          label="Expiry Date"
                          rules={[
                            { required: true, message: 'Expiry date is required' },
                            {
                              validator: (_, value) => {
                                if (value && new Date(value) < new Date(new Date().setHours(0, 0, 0, 0))) {
                                  return Promise.reject(new Error('Expiry date cannot be in the past'));
                                }
                                return Promise.resolve();
                              }
                            }
                          ]}
                          getValueProps={(value) => ({
                            value: value ? new Date(value).toISOString().split('T')[0] : ''
                          })}
                          style={{ marginBottom: 0 }}
                        >
                          <Input type="date" placeholder="Expiry Date" style={{ width: '100%' }} min={new Date().toISOString().split('T')[0]} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'document_file']}
                          label="File"
                          valuePropName="fileList"
                          initialValue={[]}
                          getValueProps={(value) => {
                            if (Array.isArray(value) && value.length > 0) {
                              return value.map(item => {
                                if (typeof item === 'string') {
                                  const url = item.startsWith('http') ? item : `${imagepath}${item}`;
                                  return {
                                    uid: item,
                                    name: item.split('/').pop(),
                                    status: 'done',
                                    url: url,
                                    thumbUrl: url,
                                  };
                                }
                                const fileUrl = item.url || (typeof item.document_file === 'string' ? (item.document_file.startsWith('http') ? item.document_file : `${imagepath}${item.document_file}`) : '');
                                return {
                                  ...item,
                                  url: fileUrl || item.url,
                                  thumbUrl: fileUrl || item.thumbUrl || item.url,
                                };
                              });
                            }
                            if (typeof value === 'string' && value.trim() !== '') {
                              const fileUrl = value.startsWith('http') ? value : `${imagepath}${value}`;
                              const fileName = value.split('/').pop() || 'Document';
                              return [{
                                uid: '-1',
                                name: fileName,
                                status: 'done',
                                url: fileUrl,
                                thumbUrl: fileUrl,
                              }];
                            }
                            return Array.isArray(value) ? value : [];
                          }}
                          getValueFromEvent={(e) => {
                            if (Array.isArray(e)) return e.slice(-1);
                            return e && e.fileList ? e.fileList.slice(-1) : [];
                          }}
                          rules={[
                            {
                              required: mode === 'add',
                              message: 'Document file is required'
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Upload
                            beforeUpload={(file) => {
                              const isAllowedFormat = /\.(pdf|jpg|jpeg|png|doc|docx)$/i.test(file.name);
                              if (!isAllowedFormat) {
                                message.error(`"${file.name}" is an unsupported format. Only PDF, JPG, PNG, and DOC files are allowed.`);
                                return Upload.LIST_IGNORE;
                              }
                              const isLt5M = file.size / 1024 / 1024 <= 5;
                              if (!isLt5M) {
                                message.error(`"${file.name}" exceeds the 5MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). Please select a file under 5MB.`);
                                return Upload.LIST_IGNORE;
                              }
                              return false;
                            }}
                            maxCount={1}
                            listType="picture"
                            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.PDF,.JPG,.PNG"
                          >
                            <Button icon={<UploadOutlined />} size="small">File</Button>
                          </Upload>
                        </Form.Item>
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                          Formats: PDF, JPG, PNG (Max 5MB)
                        </div>
                        {existingUrl && (() => {
                          const isImageFile = /\.(jpg|jpeg|png|gif|webp)$/i.test(existingName || existingUrl);
                          return (
                            <div style={{ marginTop: 8 }}>
                              <a
                                href={existingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justify: 'space-between',
                                  width: '100%',
                                  padding: '6px 10px',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: 6,
                                  backgroundColor: '#ffffff',
                                  color: '#1890ff',
                                  fontSize: 13,
                                  textDecoration: 'none',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
                                  {isImageFile ? (
                                    <img
                                      src={existingUrl}
                                      alt={existingName}
                                      style={{
                                        width: 36,
                                        height: 36,
                                        objectFit: 'cover',
                                        borderRadius: 4,
                                        border: '1px solid #e8e8e8',
                                        flexShrink: 0
                                      }}
                                    />
                                  ) : (
                                    <FileOutlined style={{ fontSize: 24, color: '#1890ff', flexShrink: 0 }} />
                                  )}
                                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 400 }}>
                                    {existingName}
                                  </span>
                                </div>
                                <EyeOutlined style={{ color: '#8c8c8c', marginLeft: 8, flexShrink: 0, fontSize: 16 }} />
                              </a>
                            </div>
                          );
                        })()}
                      </Col>
                      <Col span={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 30 }}>
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', fontSize: 18, cursor: 'pointer' }} />
                      </Col>
                    </Row>
                  );
                })}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Document
                  </Button>
                </Form.Item>
              </div>
            )}
          </Form.List>
        </Card>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>{mode === 'edit' ? 'Update Employee' : 'Create Employee'}</Button>
          <Button style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default SubcontractorEmployeeForm; 